/**
 * Stock Movements Page
 * @see US-11.8 - Page Mouvements Stock
 * @see sprint-datagrid-migration Phase 3.5
 */

import { useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ClipboardList,
} from 'lucide-react';
import {
  Badge,
  Select,
  EmptyState,
  SearchAutocomplete,
  DatePicker,
} from '@/components/ui';
import {
  DataGrid,
  textColumn,
  numberColumn,
  dateTimeColumn,
  fetchAllForExport,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { getStockMovements } from '../services/stock-movements.service';
import type { DateRange } from '@/components/ui';
import type { SearchAutocompleteOption } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatQuantity } from '@/lib/format';
import { useFormatDate } from '@/hooks';
import { formatDateParam } from '@/components/ui';
import { useStockMovements } from '../hooks';
import { useRawProductsSearch } from '@/features/recipes/hooks/useProductsSearch';
import {
  MovementType,
  MovementReason,
  SourceType,
  MOVEMENT_REASON_LABELS,
  MOVEMENT_REASON_COLORS,
} from '@bstock/shared';
import type { QueryStockMovementsDto, StockMovementListItem } from '@bstock/shared';

const DEFAULT_LIMIT = 20;

const typeOptions = [
  { value: '', label: 'Tous les types' },
  { value: 'IN', label: 'Entrees' },
  { value: 'OUT', label: 'Sorties' },
];

const reasonOptions = [
  { value: '', label: 'Toutes les raisons' },
  { value: 'RECEPTION', label: 'Reception' },
  { value: 'POS_SALE', label: 'Vente POS' },
  { value: 'LOSS', label: 'Perte' },
  { value: 'TRANSFER_IN', label: 'Transfert entrant' },
  { value: 'TRANSFER_OUT', label: 'Transfert sortant' },
  { value: 'ADJUSTMENT', label: 'Ajustement' },
  { value: 'INVENTORY', label: 'Inventaire' },
  { value: 'SUPPLIER_RETURN', label: 'Retour fournisseur' },
];

function getInitialDateRange(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function parseUrlParams(searchParams: URLSearchParams): {
  type: string;
  reason: string;
  rawProductId: string;
  dateRange: DateRange;
  page: number;
} {
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const defaultRange = getInitialDateRange();

  return {
    type: searchParams.get('type') ?? '',
    reason: searchParams.get('reason') ?? '',
    rawProductId: searchParams.get('rawProductId') ?? '',
    dateRange: {
      start: startDate ? new Date(startDate) : defaultRange.start,
      end: endDate ? new Date(endDate) : defaultRange.end,
    },
    page: parseInt(searchParams.get('page') ?? '1', 10),
  };
}

const SORT_FIELD_MAP: Record<string, QueryStockMovementsDto['sortBy']> = {
  eventDate: 'eventDate',
};

export function StockMovementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatDateTime } = useFormatDate();

  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  const [type, setType] = useState(urlParams.type);
  const [reason, setReason] = useState(urlParams.reason);
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState<QueryStockMovementsDto['sortBy']>('eventDate');
  const [sortOrder, setSortOrder] = useState<QueryStockMovementsDto['sortOrder']>('desc');

  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<SearchAutocompleteOption | null>(
    urlParams.rawProductId ? { id: urlParams.rawProductId, label: '' } : null,
  );
  const { data: productOptions = [], isLoading: isSearchingProducts } = useRawProductsSearch(productSearch);

  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const queryParams = useMemo<QueryStockMovementsDto>(
    () => ({
      page,
      limit,
      type: type ? (type as MovementType) : undefined,
      reason: reason ? (reason as MovementReason) : undefined,
      rawProductId: selectedProduct?.id,
      startDate: formatDateParam(dateRange.start) ?? undefined,
      endDate: formatDateParam(dateRange.end) ?? undefined,
      sortBy,
      sortOrder,
    }),
    [page, limit, type, reason, selectedProduct?.id, dateRange, sortBy, sortOrder],
  );

  const { data, isLoading } = useStockMovements(queryParams);

  const movements = data?.data ?? [];
  const pagination = data?.pagination;

  const handleTypeChange = useCallback(
    (value: string) => {
      setType(value);
      setPage(1);
      updateUrl({ type: value || null, page: null });
    },
    [updateUrl],
  );

  const handleReasonChange = useCallback(
    (value: string) => {
      setReason(value);
      setPage(1);
      updateUrl({ reason: value || null, page: null });
    },
    [updateUrl],
  );

  const handleProductChange = useCallback(
    (option: SearchAutocompleteOption | null) => {
      setSelectedProduct(option);
      setPage(1);
      updateUrl({ rawProductId: option?.id || null, page: null });
    },
    [updateUrl],
  );

  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      setPage(1);
      updateUrl({
        startDate: formatDateParam(range.start),
        endDate: formatDateParam(range.end),
        page: null,
      });
    },
    [updateUrl],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      updateUrl({ page: String(newPage) });
    },
    [updateUrl],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      setLimit(newLimit);
      setPage(1);
      updateUrl({ page: null });
    },
    [updateUrl],
  );

  const handleRowClick = useCallback(
    (row: StockMovementListItem) => {
      switch (row.sourceType) {
        case SourceType.GOODS_RECEIPT:
          if (row.parentSourceId) navigate(`/achats/receptions/${row.parentSourceId}`);
          break;
        case SourceType.THEORETICAL_CONSUMPTION:
          if (row.parentSourceId) navigate(`/controles/ventes-pos?receiptId=${row.parentSourceId}`);
          break;
        case SourceType.INVENTORY_LINE:
          if (row.parentSourceId) navigate(`/stock/inventaires/${row.parentSourceId}/details`);
          break;
        case SourceType.LOSS:
          navigate(`/stock/pertes/${row.id}`);
          break;
        case SourceType.TRANSFER:
          if (row.parentSourceId) navigate(`/stock/transferts/${row.parentSourceId}`);
          break;
        case SourceType.SUPPLIER_RETURN_ITEM:
          if (row.parentSourceId) navigate(`/achats/retours-fournisseur/${row.parentSourceId}`);
          break;
        default:
          break;
      }
    },
    [navigate],
  );

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(([, v]) => v === sortBy)?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        setSortBy('eventDate');
        setSortOrder('desc');
      } else {
        const backendField = SORT_FIELD_MAP[next.columnId];
        if (backendField) {
          setSortBy(backendField);
          setSortOrder(next.direction);
        }
      }
      setPage(1);
    },
    [],
  );

  // ---- Columns -----------------------------------------------------------
  const columns = useMemo<DataGridColumn<StockMovementListItem>[]>(
    () => [
      dateTimeColumn<StockMovementListItem>({
        id: 'eventDate',
        header: 'Date',
        accessor: (r) => new Date(r.eventDate),
      }),
      textColumn<StockMovementListItem>({
        id: 'type',
        header: 'Type',
        sortable: false,
        exportValue: (r) => (r.type === MovementType.IN ? 'Entree' : 'Sortie'),
        cell: (r) => (
          <div className="flex items-center gap-1.5">
            {r.type === MovementType.IN ? (
              <ArrowDownCircle className="w-4 h-4 text-green-500" />
            ) : (
              <ArrowUpCircle className="w-4 h-4 text-red-500" />
            )}
            <span className="text-sm text-neutral-600">
              {r.type === MovementType.IN ? 'Entree' : 'Sortie'}
            </span>
          </div>
        ),
      }),
      textColumn<StockMovementListItem>({
        id: 'reason',
        header: 'Raison',
        sortable: false,
        exportValue: (r) => MOVEMENT_REASON_LABELS[r.reason],
        cell: (r) => (
          <div>
            <Badge variant={MOVEMENT_REASON_COLORS[r.reason]}>
              {MOVEMENT_REASON_LABELS[r.reason]}
            </Badge>
            <div className="text-xs font-mono text-neutral-400 mt-1">
              {r.sourceLabel ?? r.sourceId.substring(0, 8)}
            </div>
          </div>
        ),
      }),
      textColumn<StockMovementListItem>({
        id: 'rawProduct',
        header: 'Produit',
        sortable: false,
        exportValue: (r) => r.rawProduct.name,
        ellipsis: true,
        maxWidth: '220px',
        cell: (r) => (
          <div>
            <div className="text-sm font-medium text-neutral-800">{r.rawProduct.name}</div>
            <div className="text-xs text-neutral-400 mt-0.5 font-mono">{r.rawProduct.sku}</div>
          </div>
        ),
      }),
      textColumn<StockMovementListItem>({
        id: 'category',
        header: 'Categorie',
        sortable: false,
        accessor: (r) => r.rawProduct.category.name,
      }),
      numberColumn<StockMovementListItem>({
        id: 'signedQuantity',
        header: 'Quantite',
        accessor: 'signedQuantity',
        sortable: false,
        precision: 3,
        cell: (r) => {
          const isIn = r.type === MovementType.IN;
          const prefix = isIn ? '+' : '';
          return (
            <span className={cn('font-semibold', isIn ? 'text-green-600' : 'text-red-600')}>
              {prefix}{formatQuantity(r.signedQuantity)}
            </span>
          );
        },
      }),
      textColumn<StockMovementListItem>({
        id: 'unit',
        header: 'Unite',
        align: 'center',
        sortable: false,
        accessor: (r) => r.rawProduct.recipeUnit.symbol,
      }),
    ],
    [formatDateTime],
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ClipboardList className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-dark-500">
                Journal des mouvements de stock
              </h1>
              <p className="text-sm text-neutral-500 mt-0.5">
                Historique de tous les mouvements affectant le stock
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-72">
            <DatePicker
              showTime
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder="Selectionner une periode"
            />
          </div>
          <div className="w-64">
            <SearchAutocomplete
              placeholder="Rechercher un produit..."
              value={selectedProduct}
              onChange={handleProductChange}
              options={productOptions}
              isLoading={isSearchingProducts}
              searchValue={productSearch}
              onSearchChange={setProductSearch}
              emptyMessage="Aucun produit trouve"
              minSearchLength={2}
            />
          </div>
          <div className="w-40">
            <Select
              value={type}
              onChange={handleTypeChange}
              options={typeOptions}
              placeholder="Tous les types"
              allowEmpty={false}
            />
          </div>
          <div className="w-44">
            <Select
              value={reason}
              onChange={handleReasonChange}
              options={reasonOptions}
              placeholder="Toutes les raisons"
              allowEmpty={false}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {movements.length === 0 && !isLoading ? (
          <EmptyState
            icon={ClipboardList}
            title="Aucun mouvement de stock"
            description="Les mouvements apparaitront apres validation des receptions ou import des ventes POS"
          />
        ) : (
          <DataGrid<StockMovementListItem>
            data={movements}
            columns={columns}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            skeletonRows={10}
            onRowClick={handleRowClick}
            sortState={sortState}
            onSortChange={handleSortChange}
            exportable={{
              fileName: { base: 'mouvements_stock' },
              sheetName: 'Mouvements',
              position: 'header',
              onFetchAllRows: () =>
                fetchAllForExport(async ({ limit: exportLimit }) => {
                  const response = await getStockMovements({
                    ...queryParams,
                    page: 1,
                    limit: exportLimit,
                  });
                  return {
                    items: response.data,
                    pagination: { total: response.pagination.total },
                  };
                }),
            }}
            pagination={
              pagination
                ? {
                    page: pagination.page,
                    limit: pagination.limit,
                    total: pagination.total,
                    totalPages: pagination.totalPages,
                    onPageChange: handlePageChange,
                    onLimitChange: handleLimitChange,
                  }
                : undefined
            }
            emptyState={
              <div className="py-8 text-center text-sm text-neutral-500">
                Aucun mouvement de stock
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}
